import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import Swal from 'sweetalert2'
import { addSubAdmins, addSubAdminPermissions, permissionModuleListings } from '../../slice/subAdminSlice'

// Validation Schema for Sub-Admin
const subAdminSchema = yup.object().shape({
    email: yup
        .string()
        .required('Email is required')
        .email('Please enter a valid email address'),
    
    password: yup
        .string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
            'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
        ),
    
    confirm_password: yup
        .string()
        .required('Please confirm your password')
        .oneOf([yup.ref('password')], 'Passwords must match'),
})

const AddSubAdmin = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { permissionModuleList } = useSelector(state => state.subAdmin)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [currentStep, setCurrentStep] = useState(1) // 1: Account Info, 2: Permissions
    const [createdSubAdminId, setCreatedSubAdminId] = useState(null)
    
    // Permission state - track each module's permissions
    const [permissions, setPermissions] = useState({})

    // Fetch permission modules on component mount
    useEffect(() => {
        dispatch(permissionModuleListings())
    }, [dispatch])

    // Initialize permissions when modules are loaded
    useEffect(() => {
        if (permissionModuleList && permissionModuleList.length > 0) {
            const initialPermissions = permissionModuleList.reduce((acc, module) => {
                acc[module.id] = {
                    permission_module_id: module.id,
                    is_view: 0,
                    is_add: 0,
                    is_edit: 0,
                    is_delete: 0,
                    is_status: 0,
                }
                return acc
            }, {})
            setPermissions(initialPermissions)
        }
    }, [permissionModuleList])

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        resolver: yupResolver(subAdminSchema),
        defaultValues: {
            email: '',
            password: '',
            confirm_password: '',
        }
    })

    // Handle sub-admin creation (Step 1)
    const onSubmitAccount = async (data) => {
        setIsSubmitting(true)
      
        try {
            const payload = {
                email: data.email,
                password: data.password,
            }
      
            const response = await dispatch(addSubAdmins(payload))
      
            if (response?.payload?.code === 200) {
                const subAdminId = response?.payload?.data?.id
                setCreatedSubAdminId(subAdminId)
                
                Swal.fire({
                    icon: 'success',
                    title: 'Sub-Admin Created!',
                    text: 'Now assign permissions to the sub-admin.',
                    timer: 2000,
                    showConfirmButton: false,
                })

                // Move to permissions step
                setCurrentStep(2)
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.payload?.message || 'Failed to create sub-admin',
                })
            }
        } catch (error) {
            console.error(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Something went wrong',
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    // Handle permission change for a specific module
    const handlePermissionChange = (moduleId, permissionType) => {
        setPermissions(prev => ({
            ...prev,
            [moduleId]: {
                ...prev[moduleId],
                [permissionType]: prev[moduleId][permissionType] === 1 ? 0 : 1
            }
        }))
    }

    // Handle "Select All" for a module
    const handleSelectAll = (moduleId) => {
        const currentModule = permissions[moduleId]
        const allSelected = currentModule.is_view === 1 && 
                          currentModule.is_add === 1 && 
                          currentModule.is_edit === 1 && 
                          currentModule.is_delete === 1 && 
                          currentModule.is_status === 1

        setPermissions(prev => ({
            ...prev,
            [moduleId]: {
                ...prev[moduleId],
                is_view: allSelected ? 0 : 1,
                is_add: allSelected ? 0 : 1,
                is_edit: allSelected ? 0 : 1,
                is_delete: allSelected ? 0 : 1,
                is_status: allSelected ? 0 : 1,
            }
        }))
    }

    // Submit permissions (Step 2)
    const onSubmitPermissions = async () => {
        setIsSubmitting(true)

        try {
            // Convert permissions object to array format
            const permissionsArray = Object.values(permissions).filter(perm => 
                perm.is_view === 1 || 
                perm.is_add === 1 || 
                perm.is_edit === 1 || 
                perm.is_delete === 1 || 
                perm.is_status === 1
            )

            if (permissionsArray.length === 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'No Permissions Selected',
                    text: 'Please select at least one permission before proceeding.',
                })
                setIsSubmitting(false)
                return
            }

            const payload = {
                subadmin_id: createdSubAdminId,
                permissions: permissionsArray
            }

            const response = await dispatch(addSubAdminPermissions(payload))

            if (response?.payload?.code === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Sub-admin created with permissions successfully!',
                    timer: 2000,
                    showConfirmButton: false,
                })

                reset()
                setTimeout(() => navigate('/subadmins'), 2000)
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.payload?.message || 'Failed to assign permissions',
                })
            }
        } catch (error) {
            console.error(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Something went wrong',
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    // Skip permissions and finish
    const handleSkipPermissions = () => {
        Swal.fire({
            title: 'Skip Permissions?',
            text: 'The sub-admin will be created without any permissions. You can assign them later.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#0d6efd',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, skip',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    icon: 'success',
                    title: 'Sub-Admin Created',
                    text: 'Sub-admin created successfully without permissions.',
                    timer: 2000,
                    showConfirmButton: false,
                })
                setTimeout(() => navigate('/subadmins'), 2000)
            }
        })
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Add Sub-Admin</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/subadmins')}
                    style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        borderRadius: '6px'
                    }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Progress Steps */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '8px' }}>
                <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-center">
                        <div className="d-flex align-items-center">
                            <div 
                                className={`rounded-circle d-flex align-items-center justify-content-center ${
                                    currentStep >= 1 ? 'bg-primary text-white' : 'bg-light text-muted'
                                }`}
                                style={{ width: '40px', height: '40px', fontWeight: 'bold' }}
                            >
                                1
                            </div>
                            <span className={`ms-2 ${currentStep >= 1 ? 'fw-semibold' : 'text-muted'}`}>
                                Account Information
                            </span>
                        </div>

                        <div 
                            className="mx-3"
                            style={{ 
                                width: '80px', 
                                height: '2px', 
                                backgroundColor: currentStep >= 2 ? '#0d6efd' : '#dee2e6' 
                            }}
                        />

                        <div className="d-flex align-items-center">
                            <div 
                                className={`rounded-circle d-flex align-items-center justify-content-center ${
                                    currentStep >= 2 ? 'bg-primary text-white' : 'bg-light text-muted'
                                }`}
                                style={{ width: '40px', height: '40px', fontWeight: 'bold' }}
                            >
                                2
                            </div>
                            <span className={`ms-2 ${currentStep >= 2 ? 'fw-semibold' : 'text-muted'}`}>
                                Assign Permissions
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Step 1: Account Information */}
            {currentStep === 1 && (
                <form onSubmit={handleSubmit(onSubmitAccount)}>
                    <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Account Information
                        </div>
                        <div className="card-body p-4">
                            <div className="row g-3">
                                {/* Email */}
                                <div className="col-md-12">
                                    <label className="form-label">
                                        Email <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                        placeholder="Enter email address"
                                        {...register('email')}
                                    />
                                    {errors.email && (
                                        <div className="invalid-feedback">{errors.email.message}</div>
                                    )}
                                </div>

                                {/* Password */}
                                <div className="col-md-6">
                                    <label className="form-label">
                                        Password <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                        placeholder="Enter password"
                                        {...register('password')}
                                    />
                                    {errors.password && (
                                        <div className="invalid-feedback">{errors.password.message}</div>
                                    )}
                                    <small className="text-muted">
                                        Password must contain uppercase, lowercase, number, and special character
                                    </small>
                                </div>

                                {/* Confirm Password */}
                                <div className="col-md-6">
                                    <label className="form-label">
                                        Confirm Password <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        className={`form-control ${errors.confirm_password ? 'is-invalid' : ''}`}
                                        placeholder="Confirm password"
                                        {...register('confirm_password')}
                                    />
                                    {errors.confirm_password && (
                                        <div className="invalid-feedback">{errors.confirm_password.message}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="d-flex gap-3 justify-content-end mt-4">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => navigate('/subadmins')}
                            disabled={isSubmitting}
                            style={{
                                padding: '10px 24px',
                                fontSize: '14px',
                                borderRadius: '6px'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                            style={{
                                padding: '10px 24px',
                                fontSize: '14px',
                                borderRadius: '6px',
                                minWidth: '150px'
                            }}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Creating...
                                </>
                            ) : (
                                'Next: Assign Permissions →'
                            )}
                        </button>
                    </div>
                </form>
            )}

            {/* Step 2: Assign Permissions */}
            {currentStep === 2 && (
                <div>
                    <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0 d-flex justify-content-between align-items-center" style={{ borderRadius: '8px 8px 0 0' }}>
                            <span>Assign Permissions</span>
                            <small className="text-muted">Select permissions for each module</small>
                        </div>
                        <div className="card-body p-4">
                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '25%' }}>Module Name</th>
                                            <th style={{ width: '12%', textAlign: 'center' }}>View</th>
                                            <th style={{ width: '12%', textAlign: 'center' }}>Add</th>
                                            <th style={{ width: '12%', textAlign: 'center' }}>Edit</th>
                                            <th style={{ width: '12%', textAlign: 'center' }}>Delete</th>
                                            <th style={{ width: '12%', textAlign: 'center' }}>Status</th>
                                            <th style={{ width: '15%', textAlign: 'center' }}>Select All</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {permissionModuleList && permissionModuleList.length > 0 ? (
                                            permissionModuleList.map((module) => {
                                                const modulePerms = permissions[module.id]
                                                if (!modulePerms) return null

                                                const allSelected = modulePerms.is_view === 1 && 
                                                                  modulePerms.is_add === 1 && 
                                                                  modulePerms.is_edit === 1 && 
                                                                  modulePerms.is_delete === 1 && 
                                                                  modulePerms.is_status === 1

                                                return (
                                                    <tr key={module.id}>
                                                        <td className="fw-semibold">{module.display_name}</td>
                                                        
                                                        {/* View */}
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={modulePerms.is_view === 1}
                                                                onChange={() => handlePermissionChange(module.id, 'is_view')}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </td>

                                                        {/* Add */}
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={modulePerms.is_add === 1}
                                                                onChange={() => handlePermissionChange(module.id, 'is_add')}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </td>

                                                        {/* Edit */}
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={modulePerms.is_edit === 1}
                                                                onChange={() => handlePermissionChange(module.id, 'is_edit')}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </td>

                                                        {/* Delete */}
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={modulePerms.is_delete === 1}
                                                                onChange={() => handlePermissionChange(module.id, 'is_delete')}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </td>

                                                        {/* Status */}
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={modulePerms.is_status === 1}
                                                                onChange={() => handlePermissionChange(module.id, 'is_status')}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </td>

                                                        {/* Select All */}
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button
                                                                type="button"
                                                                className={`btn btn-sm ${allSelected ? 'btn-primary' : 'btn-outline-primary'}`}
                                                                onClick={() => handleSelectAll(module.id)}
                                                                style={{ fontSize: '12px' }}
                                                            >
                                                                {allSelected ? 'Deselect All' : 'Select All'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4 text-muted">
                                                    {isSubmitting ? 'Loading modules...' : 'No permission modules available'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Permission Summary */}
                            <div className="mt-4 p-3 bg-light rounded">
                                <small className="text-muted">
                                    <strong>Note:</strong> The sub-admin will only be able to access modules and perform actions based on the permissions selected above.
                                </small>
                            </div>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="d-flex gap-3 justify-content-between mt-4">
                        <button
                            type="button"
                            className="btn btn-outline-warning"
                            onClick={handleSkipPermissions}
                            disabled={isSubmitting}
                            style={{
                                padding: '10px 24px',
                                fontSize: '14px',
                                borderRadius: '6px'
                            }}
                        >
                            Skip Permissions
                        </button>

                        <div className="d-flex gap-3">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => setCurrentStep(1)}
                                disabled={isSubmitting}
                                style={{
                                    padding: '10px 24px',
                                    fontSize: '14px',
                                    borderRadius: '6px'
                                }}
                            >
                                ← Back
                            </button>
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={onSubmitPermissions}
                                disabled={isSubmitting}
                                style={{
                                    padding: '10px 24px',
                                    fontSize: '14px',
                                    borderRadius: '6px',
                                    minWidth: '180px'
                                }}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Saving...
                                    </>
                                ) : (
                                    'Complete & Create Sub-Admin'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AddSubAdmin